import { IconButton, Stack, Typography, useMediaQuery } from "@mui/material"
import React, { useState, useCallback } from "react"
import { styles } from "./styles/LibraryItem.styles"
import StarRoundedIcon from "@mui/icons-material/StarRounded"
import InfoIcon from "@mui/icons-material/Info"
import Image from "components/Image"
import useApi from "modules/library"
import ExplanationModal from "components/Tools/ExplanationModal"
import { maxWidth600 } from "themes/default/breakpoints"
import { decode } from "html-entities"
import useTour from "hooks/useTour"
import useSoundHook from "hooks/useSound"
import { cardHover, select } from "utils/sounds"
import useSound from "use-sound"
import ChooseGameModal from "pages/student/smashgames/ChooseGameModal"
import { useAuthContext } from "contexts/AuthContext"

export default function LibraryItem({
  item,
  onClose,
  showTour,
  showButtons = true,
  onExplanationClick,
  onGameClick,
  moduleTypeFlex = 1 / 3,
  buttonsFlex = 1 / 6,
  fontSettings = {},
}) {
  const { audioSettings } = useAuthContext()
  const { setIsOpen, isOpen } = useTour()
  const [isFavorite, setIsFavorite] = useState(item.isFavorite)
  const [isExplanationOpen, setIsExplanationOpen] = useState(false)
  const { setFavorite } = useApi()
  const matches = useMediaQuery(maxWidth600)
  const [playSelect] = useSoundHook(select, {
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playHover] = useSound(cardHover, {
    interrupt: true,
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [showModuleLink, setShowModuleLink] = useState(false)
  const [gameOpen, setGameOpen] = useState(false)

  const onFavoriteClick = useCallback(
    (e) => {
      e.stopPropagation()
      setIsFavorite(!isFavorite)
      setFavorite({
        moduleId: item.moduleId,
        isFavorite: !isFavorite,
      })
    },
    [item, isFavorite]
  )

  const onInfoClick = useCallback(
    (e) => {
      e.stopPropagation()
      setShowModuleLink(!showModuleLink)
    },
    [showModuleLink]
  )

  const showExplanation = useCallback(
    (e) => {
      e.stopPropagation()
      setIsOpen(false)
      playSelect()
      setIsExplanationOpen(true)
    },
    [item, setIsOpen]
  )

  const timer = React.useRef()

  const onHover = useCallback(
    (e) => {
      timer.current = setTimeout(() => {
        playHover()
        clearTimeout(timer.current)
      }, 100)
    },
    [playHover]
  )

  // const openMemoramaGame = useCallback(
  //   async (settings) => {
  //     onClose()
  //     setGameOpen(false)
  //     // navigate to /games/memorama and send props to memorama game with react router
  //     navigate(routes.MEMORAMA, {
  //       item: item.moduleId,
  //       state: {
  //         moduleId: item.moduleId,
  //         title: item.moduleName,
  //         settings,
  //       },
  //     })
  //   },
  //   [item]
  // )

  return (
    <Stack
      sx={[
        styles.item,
        fontSettings?.bgColor && { backgroundColor: fontSettings.bgColor },
      ]}
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      spacing={2}
      className={`module-item-${item.moduleId}`}
    >
      <ExplanationModal
        open={isExplanationOpen}
        onClose={() => {
          setIsExplanationOpen(false)
          if (showTour) {
            onClose()
          }
        }}
        moduleId={item.moduleId}
        moduleTypeId={item?.moduleTypeId}
        title={item?.moduleName}
        showTour={showTour}
        fontSettings={fontSettings}
      />
      <ChooseGameModal
        visible={gameOpen}
        onClose={() => setGameOpen(false)}
        data={item}
        onGameOpen={() => {
          setGameOpen(false)
          onClose()
        }}
        variant={"simple"}
        fontSettings={fontSettings}
      />
      {/* <GamesModal
        open={gameOpen}
        onClose={() => setGameOpen(false)}
        showWordsGames={item.hasMemorama}
        item={item}
        onGameOpen={() => {
          setGameOpen(false)
          onClose()
        }}
      /> */}
      {showButtons && (
        <Stack direction={"row"} className={`module-item-${item.moduleId}`}>
          <IconButton onClick={onFavoriteClick} sx={{ p: 0.25 }}>
            <StarRoundedIcon
              sx={isFavorite ? styles.starSelected : styles.star}
            />
          </IconButton>
          <IconButton sx={{ p: 0.25 }} onClick={onInfoClick}>
            <InfoIcon color="primary" />
          </IconButton>
        </Stack>
      )}
      <Stack flex={1} className={`module-item-${item.moduleId}`}>
        {!showModuleLink ? (
          <Typography variant="span" className={`module-item-${item.moduleId}`}>
            {decode(item.moduleName)}
          </Typography>
        ) : (
          <Typography variant="span" className={`module-item-${item.moduleId}`}>
            {decode(
              `${item.templateName} > ${item.levelName} > ${item.unitName} > ${item.moduleName}`
            )}
          </Typography>
        )}
        {matches && (
          <Typography variant="span" className={`module-item-${item.moduleId}`}>
            {decode(item.moduleTypeName)}
          </Typography>
        )}
      </Stack>
      {!matches && (
        <Typography
          variant="span"
          flex={moduleTypeFlex}
          textAlign={moduleTypeFlex === 1 ? "center" : "left"}
          className={`module-item-${item.moduleId}`}
        >
          {item.moduleTypeName}
        </Typography>
      )}
      <Stack
        direction={"row"}
        flex={buttonsFlex}
        justifyContent={"flex-end"}
        spacing={1}
        className={`module-item-${item.moduleId}`}
      >
        {item.hasExplanation && (
          <IconButton
            sx={styles.hoverButton}
            onClick={showExplanation}
            onMouseEnter={onHover}
            onMouseLeave={() => clearTimeout(timer.current)}
            className={`explanation-button-${item.moduleTypeId}`}
          >
            <Image
              name={"searchIcon"}
              wrapperClassName="icon"
              width={matches ? 25 : 35}
              height={matches ? 25 : 35}
            />
            <Image
              name={"searchIconActive"}
              wrapperClassName="active-icon"
              width={matches ? 25 : 35}
              height={matches ? 25 : 35}
            />
          </IconButton>
        )}
        <IconButton
          sx={styles.hoverButton}
          onMouseEnter={onHover}
          onMouseLeave={() => clearTimeout(timer.current)}
          onClick={() => {
            if (onGameClick) {
              playSelect()
              onGameClick(item)
            } else {
              setGameOpen(true)
            }
          }}
          disabled={isOpen}
        >
          <Image
            name={"gameIcon"}
            wrapperClassName="icon"
            width={matches ? 25 : 35}
            height={matches ? 25 : 35}
          />
          <Image
            name={"gameIconActive"}
            wrapperClassName="active-icon"
            width={matches ? 25 : 35}
            height={matches ? 25 : 35}
          />
        </IconButton>
      </Stack>
    </Stack>
  )
}
